import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Upload,
  Avatar,
  Row,
  Col,
  Typography,
  Space,
  Progress,
  Tooltip,
} from 'antd';
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  SaveOutlined,
  UserOutlined,
  UploadOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { request } from '@umijs/max';
import authService from '@/services/AuthService';
import UserConfigService from '@/services/UserConfigService';

const { Title, Text } = Typography;
const { Dragger } = Upload;

export interface ChangePasswordProps {
  visible: boolean;
  onClose: () => void;
}

const API_BASE = process.env.UMI_APP_API_URL || 'http://localhost:1337';

const passwordStrength = (pw: string) => {
  if (!pw) return { percent: 0, status: 'normal', label: 'Vacía' };
  let score = 0;
  if (pw.length >= 6) score += 1;
  if (pw.length >= 10) score += 1;
  if (/[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  const percent = Math.min(100, (score / 5) * 100);
  const status = percent < 40 ? 'exception' : percent < 80 ? 'normal' : 'success';
  const label = percent < 40 ? 'Débil' : percent < 80 ? 'Media' : 'Fuerte';
  return { percent, status, label };
};

const ChangePassword: React.FC<ChangePasswordProps> = ({ visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [newPwdValue, setNewPwdValue] = useState<string>('');

  useEffect(() => {
    const load = async () => {
      try {
        const session = authService.getCurrentSession();
        const userId = session?.fk_user;
        // Some session shapes use `name` or `user` — cast to any for safety
        const s: any = session;
        setUserName(s?.name || s?.user || session?.email || 'Usuario');
        setUserEmail(session?.email || '');
        if (userId) {
          const url = await UserConfigService.getUserPhotoDataUrl(userId);
          if (url) setPhotoPreview(url);
        }
      } catch {
        // ignore preview errors
      }
    };
    if (visible) {
      load();
      form.resetFields();
      setPhotoFile(null);
      setNewPwdValue('');
    }
  }, [visible, form]);

  const handleFinish = async (values: any) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        message.error('Token de autenticación no encontrado. Inicie sesión de nuevo.');
        setLoading(false);
        return;
      }

      const session = authService.getCurrentSession();
      const email = session?.email || '';

      // If newPassword provided, attempt to change password
      if (values.newPassword) {
        const payload = {
          email,
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        };
        // eslint-disable-next-line no-console
        console.log('[ChangePassword] payload before request:', payload);

        await request(`${API_BASE}/user/change-password`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          data: payload,
        });

        message.success('Contraseña cambiada exitosamente');
      }

      // If user selected a photo, upload it (independent of password change)
      if (photoFile) {
        try {
          const session = authService.getCurrentSession();
          const userId = session?.fk_user;
          if (userId) {
            await UserConfigService.uploadUserPhoto(userId, photoFile);
            message.success('Foto de perfil actualizada');
          }
        } catch (err: any) {
          console.error('Error subiendo foto:', err);
          message.error('Fallo al subir la foto');
        }
      }
      form.resetFields();
      setPhotoFile(null);
      onClose();
    } catch (err: any) {
      const text = err?.message || 'Error al cambiar la contraseña';
      message.error(text);
    } finally {
      setLoading(false);
    }
  };

  const beforeUpload = (file: File) => {
    setPhotoFile(file);
    try {
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);
    } catch {
      setPhotoPreview(null);
    }
    // Prevent auto upload by antd
    return false;
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const onNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPwdValue(e.target.value);
  };

  const strength = passwordStrength(newPwdValue);

  return (
    <Modal
      title="Mi perfil"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnHidden
      bodyStyle={{ padding: 20 }}
    >
      <Row gutter={24} align="top">
        {}
        <Col xs={24} sm={8} style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{ position: 'relative' }}>
              <Avatar size={104} src={photoPreview || undefined} icon={<UserOutlined />} />
              <div style={{ position: 'absolute', right: -6, bottom: -6 }}>
                <Upload showUploadList={false} beforeUpload={beforeUpload} accept="image/*">
                  <Button shape="circle" size="small" icon={<UploadOutlined />} aria-label="Subir foto" />
                </Upload>
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <Typography.Title level={5} style={{ margin: 0 }}>{userName}</Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>{userEmail}</Typography.Text>
            </div>

            {/* <div style={{ display: 'flex', gap: 8 }}>
              {(photoPreview || photoFile) && (
                <Tooltip title="Eliminar foto">
                  <Button icon={<DeleteOutlined />} danger size="small" onClick={handleRemovePhoto} />
                </Tooltip>
              )}
              {(photoPreview || photoFile) && (
                <Button size="small" onClick={() => photoPreview && window.open(photoPreview, '_blank')}>Ver</Button>
              )}
            </div> */}
          </div>
        </Col>

        {}
        <Col xs={24} sm={16}>
          <Form form={form} layout="vertical" onFinish={handleFinish}>
            <Form.Item name="currentPassword" label="Contraseña actual" rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const newPassword = getFieldValue('newPassword');
                  if (newPassword && !value) {
                    return Promise.reject(new Error('Ingrese la contraseña actual para cambiarla'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}>
              <Input.Password placeholder="Contraseña actual" iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)} />
            </Form.Item>

            <Form.Item name="newPassword" label="Nueva contraseña (opcional)" rules={[{ min: 6, message: 'La contraseña debe tener al menos 6 caracteres' }]}>
              <Input.Password placeholder="Nueva contraseña" iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)} onChange={onNewPasswordChange} />
            </Form.Item>

            {newPwdValue ? (
              <Form.Item>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <Progress percent={strength.percent} status={strength.status as any} showInfo={false} strokeWidth={8} />
                    <Typography.Text type={strength.status === 'exception' ? 'danger' : undefined}>{strength.label}</Typography.Text>
                  </div>
                </div>
              </Form.Item>
            ) : null}

            <Form.Item name="confirmPassword" label="Confirmar nueva contraseña" dependencies={['newPassword']} rules={[({ getFieldValue }) => ({
              validator(_, value) {
                const newPassword = getFieldValue('newPassword');
                if (!newPassword) return Promise.resolve();
                if (!value) return Promise.reject(new Error('Confirme la nueva contraseña'));
                if (newPassword === value) return Promise.resolve();
                return Promise.reject(new Error('Las contraseñas no coinciden'));
              },
            })]}>
              <Input.Password placeholder="Confirmar nueva contraseña" iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)} />
            </Form.Item>

            <Form.Item style={{ marginTop: 10 }}>
              <Space style={{ float: 'right' }}>
                <Button onClick={onClose}>Cancelar</Button>
                <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>Guardar</Button>
              </Space>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </Modal>
  );
};

export default ChangePassword;
